// ===================================================================
// PHOENIX OS — CHECKOUT ASAAS EMBUTIDO (Dashboard)
// ===================================================================
// Componente único, reaproveitado nos dois Dashboards (Gestor e
// Consultor) — mesma lógica de pagamento que o app já usa
// (asaasService.js -> Cloud Functions), só que embutido direto na tela,
// sem precisar sair pro app pra comprar/fazer upgrade.
//
// Como usar:
//   <CheckoutAsaasCard
//     planos={PLANOS_GESTOR}          // array de { id, name, price, type }
//     planoSelecionadoId="tactical"   // qual vem pré-selecionado
//     tipoPerfil="Gestor"             // "Gestor" | "Consultor" — decide em
//                                     // quais campos do Firestore grava
//     onSuccess={(planId) => {...}}  // chamado depois do plano liberado
//     onClose={() => {...}}          // opcional — some o botão "Cancelar" se ausente
//   />
//
// Pode ser usado tanto dentro de um modal quanto embutido direto numa
// aba (ex: Configurações) — o componente em si não tem "moldura" de
// modal, só o cartão de formulário. Quem chama decide a moldura.
// ===================================================================

import React, { useState } from 'react';
import AceiteAtivo, { registrarAceite } from './AceiteAtivo';
import { CreditCard, QrCode, FileText, Loader2, ShieldCheck, AlertTriangle, Check } from 'lucide-react';
import { criarClienteAsaas, criarAssinaturaAsaas, criarCobrancaAvulsaAsaas } from '../services/asaasService';
import { db, auth } from '../services/firebase';
import { doc, setDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

// Mesmo validador de CPF/CNPJ do app (cópia local de propósito — Dashboard
// e App são mundos separados, sem import cruzado entre si).
function cpfCnpjValido(valor) {
  const numeros = String(valor || '').replace(/\D/g, '');

  if (numeros.length === 11) {
    if (/^(\d)\1{10}$/.test(numeros)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += Number(numeros[i]) * (10 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== Number(numeros[9])) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += Number(numeros[i]) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    return resto === Number(numeros[10]);
  }

  if (numeros.length === 14) {
    if (/^(\d)\1{13}$/.test(numeros)) return false;
    const calc = (base) => {
      let pesos = base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      let soma = 0;
      for (let i = 0; i < base.length; i++) soma += Number(base[i]) * pesos[i];
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };
    const dv1 = calc(numeros.slice(0, 12));
    if (dv1 !== Number(numeros[12])) return false;
    const dv2 = calc(numeros.slice(0, 13));
    return dv2 === Number(numeros[13]);
  }

  return false;
}

export default function CheckoutAsaasCard({ planos, planoSelecionadoId, tipoPerfil = 'Gestor', onSuccess, onClose }) {
  // 🔥 Os hooks (useState) têm que rodar em toda renderização, na mesma
  // ordem, não importa o que aconteça — essa é a "Regra dos Hooks" do
  // React. Por isso ficam TODOS aqui em cima, antes de qualquer 'return'
  // condicional. (Bug corrigido: a blindagem contra 'planos' vazio abaixo
  // estava ANTES dos useState — quando 'planos' passava de vazio pra
  // preenchido no meio da mesma sessão de checkout aberto, o React
  // quebrava o componente inteiro no meio da tela, silenciosamente,
  // impedindo o pagamento de completar. Foi provavelmente a causa do
  // "comprei e não registrou no Asaas".)
  const [planoId, setPlanoId] = useState(planoSelecionadoId || planos?.[0]?.id);
  const [email, setEmail] = useState(auth.currentUser?.email || '');
  const [cpfCnpj, setCpfCnpj] = useState('');
  // 🔥 Antes o aviso de "CPF inválido" só aparecia DEPOIS de clicar em
  // pagar, numa caixa lá embaixo, longe do campo — fácil de não perceber
  // qual campo causou o erro. Agora aparece direto embaixo do campo,
  // assim que a pessoa termina de digitar (não incomoda enquanto ela
  // ainda está no meio da digitação).
  const [cpfTocado, setCpfTocado] = useState(false);
  const cpfPreenchido = cpfCnpj.trim().length > 0;
  const cpfInvalidoAoVivo = cpfTocado && cpfPreenchido && !cpfCnpjValido(cpfCnpj);
  const [telefone, setTelefone] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('PIX');
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');
  const [aceite, setAceite] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  // 🔥 BLINDAGEM: se quem chamou esqueceu de checar se existe plano de
  // upgrade disponível (planos=[] ou undefined), mostra um aviso em vez
  // de quebrar a tela toda com "Cannot read properties of undefined
  // (reading 'name')". Aconteceu no Dashboard Consultor quando o modal
  // de checkout abria sem plano nenhum pra oferecer. Agora vem DEPOIS
  // dos hooks, então nunca quebra a regra acima.
  if (!Array.isArray(planos) || planos.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-sm text-slate-400">Nenhum plano de upgrade disponível no momento.</p>
        {typeof onClose === 'function' && (
          <button
            type="button" onClick={onClose}
            className="mt-6 bg-white/5 hover:bg-white/10 text-slate-300 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
          >
            Fechar
          </button>
        )}
      </div>
    );
  }

  const plano = planos.find(p => p.id === planoId) || planos[0];

  // 🔒 REMOVIDA (segurança): existia aqui uma função que gravava o plano
  // direto no Firestore pelo navegador. Isso agora é responsabilidade
  // exclusiva do webhookAsaas, que roda no servidor e só age depois do
  // pagamento confirmado — navegador nenhum consegue forjar isso.

  const finalizarCompra = async () => {
    setErro('');
    if (!aceite) { setErro('Marque a caixa de aceite para continuar.'); return; }
    registrarAceite('checkout', plano.id);
    if (!auth.currentUser) { setErro('Sua sessão expirou — faça login novamente.'); return; }

    const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailValido) { setErro('Informe um e-mail válido (ex: nome@exemplo.com).'); return; }
    if (!cpfCnpjValido(cpfCnpj)) { setErro('CPF ou CNPJ inválido — confira os números digitados.'); return; }

    const documentoLimpo = cpfCnpj.replace(/\D/g, '');
    const valorNumerico = parseFloat(String(plano.price).replace(/\./g, '').replace(',', '.'));
    const ehAvulso = plano.type === 'PAGAMENTO ÚNICO' || plano.id === 'entry' || plano.id === 'report_avulso';

    // Dentro do app nativo (Capacitor), window.open trava numa tela branca —
    // o WebView não sabe abrir "nova aba" de verdade. Usamos o navegador
    // nativo do Capacitor nesse caso; no navegador comum (web), mantemos o
    // comportamento antigo.
    const usandoApp = Capacitor.isNativePlatform();
    let abaFatura = null;
    if (!usandoApp) {
      abaFatura = window.open('', '_blank');
      if (abaFatura) {
        abaFatura.document.write('<p style="font-family:sans-serif;padding:40px;color:#666">Gerando sua cobrança, aguarde...</p>');
      }
    }

    setProcessando(true);
    try {
      const { customerId } = await criarClienteAsaas({
        nome: auth.currentUser.displayName || email,
        email,
        cpfCnpj: documentoLimpo,
        telefone: telefone || undefined
      });

      const resultado = ehAvulso
        ? await criarCobrancaAvulsaAsaas({
          customerId, valor: valorNumerico,
          descricao: `Phoenix OS — ${plano.name}`,
          formaPagamento, planId: plano.id
        })
        : await criarAssinaturaAsaas({
          customerId, valor: valorNumerico,
          descricao: `Phoenix OS — Assinatura ${plano.name}`,
          formaPagamento, planId: plano.id
        });

      if (!ehAvulso && resultado.subscriptionId) {
        const campoId = tipoPerfil === 'Consultor' ? 'consultorAsaasSubscriptionId' : 'gestorAsaasSubscriptionId';
        await setDoc(doc(db, 'usuarios', auth.currentUser.uid), { [campoId]: resultado.subscriptionId }, { merge: true });
      }

      if (resultado.linkFatura) {
        if (usandoApp) {
          await Browser.open({ url: resultado.linkFatura });
        } else if (abaFatura) {
          abaFatura.location.href = resultado.linkFatura;
        } else {
          window.open(resultado.linkFatura, '_blank');
        }
      }

      // 🔒 SEGURANÇA: aqui o plano era liberado NA HORA, sem esperar o
      // pagamento confirmar. Combinado com a regra do Firestore que deixa
      // cada um escrever no próprio documento, dava pra abrir o console do
      // navegador e se dar um plano caro de graça. Agora quem libera é só
      // o webhookAsaas (servidor), depois da confirmação real — e os
      // Dashboards já escutam 'usuarios/{uid}' em tempo real, então a tela
      // se atualiza sozinha assim que o pagamento cai.
      setSucesso(true);
      if (typeof onSuccess === 'function') onSuccess(plano.id);
    } catch (e) {
      console.error('[PHOENIX] Erro no checkout embutido do Dashboard:', e);
      setErro('Não foi possível processar o pagamento agora. Tente novamente em instantes.');
      if (abaFatura && !usandoApp && !abaFatura.closed) abaFatura.close();
    } finally {
      setProcessando(false);
    }
  };

  if (sucesso) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-lime-400/10 border border-lime-400/20 flex items-center justify-center">
          <Check size={28} className="text-lime-400" />
        </div>
        <h3 className="text-base font-black text-white uppercase italic tracking-tighter mb-2">Pagamento iniciado!</h3>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          Concluí o pagamento na aba que abrimos. Assim que confirmar, o plano <strong className="text-lime-400">{plano.name}</strong> é liberado automaticamente.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* SELETOR DE PLANO */}
      {planos.length > 1 && (
        <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${Math.min(planos.length, 3)}, 1fr)` }}>
          {planos.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlanoId(p.id)}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${planoId === p.id ? 'bg-lime-400/10 border-lime-400' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
            >
              <p className={`text-xs font-black uppercase tracking-wide ${planoId === p.id ? 'text-lime-400' : 'text-white'}`}>{p.name}</p>
              <p className="text-lg font-black text-white mt-1">R$ {p.price}</p>
              <p className="text-[11px] text-slate-500 uppercase tracking-widest">{p.type === 'PAGAMENTO ÚNICO' ? 'Pagamento único' : '/mês'}</p>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">E-mail</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
          />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">CPF ou CNPJ</label>
          <input
            type="text" value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)}
            onBlur={() => setCpfTocado(true)}
            placeholder="000.000.000-00"
            className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none transition-colors ${cpfInvalidoAoVivo ? 'border-red-500/60 focus:border-red-500' : 'border-white/10 focus:border-lime-400/50'}`}
          />
          {cpfInvalidoAoVivo && (
            <p className="text-[11px] text-red-400 mt-1.5">CPF ou CNPJ inválido — confira os números.</p>
          )}
        </div>
      </div>

      <div className="mb-6">
        <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Telefone (opcional)</label>
        <input
          type="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)}
          placeholder="(00) 00000-0000"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
        />
      </div>

      <div className="mb-6">
        <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-3">Forma de pagamento</label>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'PIX', label: 'Pix', Icone: QrCode },
            { id: 'CREDIT_CARD', label: 'Cartão', Icone: CreditCard },
            { id: 'BOLETO', label: 'Boleto', Icone: FileText },
          ].map(({ id, label, Icone }) => (
            <button
              key={id} type="button" onClick={() => setFormaPagamento(id)}
              className={`flex flex-col items-center gap-2 py-4 rounded-xl border transition-all ${formaPagamento === id ? 'bg-lime-400 text-black border-lime-400' : 'bg-black/30 text-slate-400 border-white/10 hover:border-white/20'}`}
            >
              <Icone size={18} />
              <span className="text-[11px] font-black uppercase tracking-widest">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {erro && (
        <div className="mb-5 flex items-start gap-2.5 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          <AlertTriangle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-300">{erro}</p>
        </div>
      )}

      <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-3">
        <ShieldCheck size={13} className="text-lime-400/70" />
        Pagamento processado pelo Asaas — o Phoenix OS nunca vê o número do seu cartão.
      </div>
      <AceiteAtivo checked={aceite} onChange={setAceite} comReembolso className="mb-5" />

      <div className="flex gap-3">
        {typeof onClose === 'function' && (
          <button
            type="button" onClick={onClose} disabled={processando}
            className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10 disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        <button
          type="button" onClick={finalizarCompra} disabled={processando || !aceite}
          className="flex-1 flex items-center justify-center gap-2 bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
        >
          {processando ? <><Loader2 size={14} className="animate-spin" /> Processando...</> : `Pagar R$ ${plano.price}`}
        </button>
      </div>
    </div>
  );
}
